import { API, graphqlOperation } from 'aws-amplify';
import { useEffect, useState } from 'react';

import { type AircraftMaintenanceLimitExtension } from '../../Admin/AircraftMaintenanceLimitExtensions/AircraftMaintenanceLimitExtension';
import { type AircraftMaintenanceLimit } from '../../Admin/AircraftMaintenanceLimits/AircraftMaintenanceLimit';
import { type CreateAircraftInput } from '../../API';
import { sanitizeId } from '../datasourceUtils';
import { FlightOps } from '../FlightLegs/FlightOpsTypes';
import { getDb } from '../LocalDB/LocalDB';
import { aircraftFromDb, aircraftItemFields } from '../LocalDB/Transformers/aircrafts';
import { type DbCreated } from '../LocalDB/types';
import { type Aircraft, type AircraftForPicker } from './types';
import { AircraftTag } from './types';

export const getAircraftsForPicker = async (): Promise<AircraftForPicker[]> => {
    return getDb().aircrafts.toArray();
};

export type GetAircraftParams = [string];
export const getAircraft = async (aircraftId: GetAircraftParams[0]): Promise<Aircraft | null> => {
    return (await getDb().aircrafts.get(aircraftId)) || null;
};

export const getAircraftMaintenanceLimits = async (aircraftId: string): Promise<AircraftMaintenanceLimit[]> => {
    return getDb().aircraftMaintenanceLimits.where({ aircraftId }).toArray();
};

export const getAircraftMaintenanceLimitExtensionsForLimits = async (
    maintenanceLimitIds: string[]
): Promise<AircraftMaintenanceLimitExtension[]> => {
    if (maintenanceLimitIds.length === 0) {
        return [];
    }
    return getDb().aircraftMaintenanceLimitExtensions.where('maintenanceLimitId').anyOf(maintenanceLimitIds).toArray();
};

export type GetAircraftForFlightParams = [string];
export const getAircraftsByIdLocalAndOnline = async (aircraftIds: string[]): Promise<Aircraft[]> => {
    const offlineAircrafts = (await getAircraftsById(aircraftIds).then((f) => f.filter(Boolean))) as Aircraft[];
    const offlineAircraftIds = new Set(offlineAircrafts.map((a) => a.id));
    const aircraftIdsToRequest = aircraftIds.filter((acId) => !offlineAircraftIds.has(acId));
    const onlineAircrafts = await getAircraftsByIdOnline(aircraftIdsToRequest);
    return [...offlineAircrafts, ...onlineAircrafts];
};

export const getAircraftsById = async (aircraftIds: string[]): Promise<Array<Aircraft | null>> => {
    const db = getDb();
    return (await db.aircrafts.bulkGet(aircraftIds)).map((a) => a ?? null);
};

export const getAircraftsByIdOnline = async (aircraftIds: string[]): Promise<Aircraft[]> => {
    if (aircraftIds.length === 0) {
        return [];
    }
    const aircraftsQuery = `query getFlights {
        ${aircraftIds.map(
            (aircraftId, index) => `
        getAircraft${index}: getAircraft(id: "${sanitizeId(aircraftId)}") {
            ${aircraftItemFields}
        }
        `
        )}
    }`;
    const gqlo = graphqlOperation(aircraftsQuery);
    const aircraftsResult = (await API.graphql(gqlo)) as {
        data: { [key: string]: (CreateAircraftInput & DbCreated) | null };
    };
    return (Object.values(aircraftsResult.data).filter(Boolean) as Array<CreateAircraftInput & DbCreated>).map(
        aircraftFromDb
    );
};

export const getAircraftForUserId = async (userId: string): Promise<Aircraft | undefined> => {
    const ac = await getDb().aircrafts.where({ userId }).toArray();
    return ac.shift();
};

export const MCC_OPTIONAL = 'optional';
export type aircraftMccOptions = typeof MCC_OPTIONAL | false | true;
export const getAircraftIsMcc = (aircraft: Aircraft, ops: FlightOps | undefined): aircraftMccOptions => {
    if (aircraft.tags.includes(AircraftTag.multiPilot)) {
        return true;
    }
    if (aircraft.tags.includes(AircraftTag.mutiPilotCat)) {
        if (ops === FlightOps.CAT) {
            return true;
        }
        return MCC_OPTIONAL;
    }
    return false;
};

export function useAircraft(aircraftId: string): Aircraft | null | undefined {
    const [aircraft, setAircraft] = useState<Aircraft | null | undefined>(undefined);
    useEffect(() => {
        let cancelled = false;
        getAircraft(aircraftId).then((aircraft) => {
            if (!cancelled) {
                setAircraft(aircraft);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [aircraftId]);
    return aircraft;
}
